import { Grid } from '@mui/material'
import React, { useRef, useCallback, useEffect, useState } from 'react'
import useOutsideClick from '../../hooks/useOutsideClick/useOutsideClick'
import { useDispatch } from 'react-redux'
import { changeGlobalMenu } from 'store/actions/ui.action'
import { IGlobalMenu, useOperationGlobalMenu } from 'router/globalRoute'
import { useHistory } from 'react-router-dom'
import EachPopUpMenu from 'components/PopUpMenu/EachPopUpMenu'
import { Show } from 'permission/Show'
import TimeClockPopup from 'components/TimeClockPopup/TimeClockPopup'
import { useAppSelector } from 'store/useStoreHook'
import { isUndefined } from 'lodash-es'
import { useScrollBlock } from 'hooks/useScrollBlock/useScrollBlock'
import ROUTE from 'constant/route'
import { useTranslation } from 'react-i18next'
import { PopUpBase, PopUpOverlay } from 'components/PopOver/PopUp'
import toast from 'utilities/toast/toast'

const GlobalMenu = () => {
    const navRef = useRef<HTMLDivElement>(null)
    const popUpRef = useRef<HTMLDivElement>(null)
    const isOpen = useAppSelector((state) => state.ui.globalMenu.isOpen)
    const dispatch = useDispatch()
    const history = useHistory()
    const [openTimeClock, setOpenTimeClock] = useState(false)
    const globalRouteWithOperationalSettings = useOperationGlobalMenu()
    const [blockScroll, allowScroll] = useScrollBlock()

    const handlingClose = useCallback(() => {
        dispatch(changeGlobalMenu(false))
    }, [dispatch])

    const handleClickOutside = useCallback(() => {
        if (isOpen) {
            handlingClose()
        }
    }, [isOpen, handlingClose])

    useEffect(() => {
        if (isOpen) {
            blockScroll()
        } else {
            allowScroll()
        }
    }, [isOpen, blockScroll, allowScroll])
    const toggleTimeClockPopup = useCallback(
        (isTimeClockOpen = false) => {
            dispatch(changeGlobalMenu(false))
            setOpenTimeClock(isTimeClockOpen)
        },
        [dispatch, setOpenTimeClock],
    )
    const goToRoute = useCallback(
        (route: string, isPopup?: boolean) => {
            dispatch(changeGlobalMenu(false))
            if (isPopup) {
                if (route === ROUTE.TIME_CLOCK_POPUP) toggleTimeClockPopup(true)

                if (route === ROUTE.CUSTOMER_FACING_DISPLAY) {
                    launchCFD()
                }
            } else history.push(route)
        },
        [dispatch, history, toggleTimeClockPopup],
    )

    const [init, unInit] = useOutsideClick(navRef, handleClickOutside)

    useEffect(() => {
        if (isOpen) {
            init()
        } else {
            unInit()
        }
        return () => {
            unInit()
        }
    }, [init, unInit, isOpen])

    const isSelected = (route: IGlobalMenu) => {
        const currPath = history.location.pathname

        if (
            !isUndefined(route.paths) &&
            currPath !== ROUTE.CASH_DRAWER_TERMINAL &&
            currPath !== ROUTE.POS_TERMINAL
        ) {
            return route.paths.some((i) => currPath.match(i))
        }
        return currPath === route.path
    }

    const { t } = useTranslation()

    const isAuthExpired = useAppSelector((s) => s.auth.isAuthExpired)

    useEffect(() => {
        if (isAuthExpired) {
            toggleTimeClockPopup()
        }
    }, [isAuthExpired, toggleTimeClockPopup])

    const launchCFD = () => {
        const protocol = 'poscfd://'
        const url = `load?url=${window.location.origin}${ROUTE.CUSTOMER_FACING_DISPLAY}`

        // Create an iframe to test the custom protocol
        const iframe = document.createElement('iframe')
        iframe.style.display = 'none'
        document.body.appendChild(iframe)

        // Set a timeout to show the download link if the app is not installed
        const timeout = setTimeout(() => {
            toast.warning(
                'It looks like the CFD app is not installed. Please <a href="https://orderwire.com/support/downloads#cfd" target="_blank" rel="noopener noreferrer">download the app</a> to utilize auto start up and full screen features.',
                null,
                12000,
            )
            document.body.removeChild(iframe)
            window.open(
                ROUTE.CUSTOMER_FACING_DISPLAY,
                '_blank',
                'noopener,noreferrer',
            )
        }, 1000)

        // Try to open the custom protocol
        iframe.src = protocol + url

        // Clear the timeout if the app is installed
        window.addEventListener('blur', () => {
            clearTimeout(timeout)
            document.body.removeChild(iframe)
        })
    }

    return (
        <>
            <TimeClockPopup
                open={openTimeClock}
                handleClose={() => toggleTimeClockPopup()}
            />
            <PopUpOverlay
                ref={popUpRef}
                id="global-menu-id"
                style={{ display: isOpen ? 'block' : 'none', zIndex: 2001 }}
            >
                <PopUpBase
                    ref={navRef}
                    style={{
                        top: 64,
                        right: 4,
                    }}
                >
                    <Grid container spacing={1}>
                        {globalRouteWithOperationalSettings.map((route) => (
                            <Show
                                when={route.requirePermission}
                                key={route.path}
                            >
                                <EachPopUpMenu
                                    menu={{
                                        name: t(route.name),
                                        icon: route.logo,
                                        menuId: route.path,
                                        isPopup: route.popUp,
                                        testIdPrefix: 'globalMenu',
                                    }}
                                    onClickEachMenu={goToRoute}
                                    isSelect={isSelected(route)}
                                    disabled={route.disabledButton}
                                />
                            </Show>
                        ))}
                    </Grid>
                </PopUpBase>
            </PopUpOverlay>
        </>
    )
}

export default GlobalMenu
